/**
 * Dual-store helpers for the backoffice auth tokens. When the user checks "Remember me"
 * we persist to localStorage (survives the browser session); when they don't, we use
 * sessionStorage (cleared when the tab/browser closes). The location of the token IS the
 * remember-me state — no separate flag needed. On read, sessionStorage wins so an
 * ephemeral session can't be silently extended by a stale localStorage copy from a
 * previous browser session.
 */

const AUTH_KEYS = ['accessToken', 'refreshToken', 'loggedUser'] as const;
export type AuthStorageKey = (typeof AUTH_KEYS)[number];

function safeWindow(): Window | null {
	return typeof window !== 'undefined' ? window : null;
}

export function getAuthItem(key: AuthStorageKey): string | null {
	const w = safeWindow();

	if (!w) return null;

	try {
		return w.sessionStorage.getItem(key) ?? w.localStorage.getItem(key);
	} catch (error) {
		console.error(`Error reading auth storage key "${key}":`, error);
		return null;
	}
}

export function setAuthItem(key: AuthStorageKey, value: string, persistent: boolean): void {
	const w = safeWindow();

	if (!w) return;

	try {
		if (persistent) {
			w.localStorage.setItem(key, value);
			w.sessionStorage.removeItem(key);
		} else {
			w.sessionStorage.setItem(key, value);
			w.localStorage.removeItem(key);
		}
	} catch (error) {
		console.error(`Error writing auth storage key "${key}":`, error);
	}
}

export function clearAuthStorage(): void {
	const w = safeWindow();

	if (!w) return;

	try {
		for (const key of AUTH_KEYS) {
			w.localStorage.removeItem(key);
			w.sessionStorage.removeItem(key);
		}
	} catch (error) {
		console.error('Error clearing auth storage:', error);
	}
}

/**
 * True when the current session is persistent (was stored with rememberMe=true). Used
 * during token refresh to keep the new tokens in the same store class as the originals.
 */
export function isPersistentSession(): boolean {
	const w = safeWindow();

	if (!w) return true;

	try {
		// If the token lives in localStorage, the session is persistent; if only in
		// sessionStorage, it's ephemeral. If neither (logged out), default to persistent so a
		// fresh refresh writes to localStorage.
		if (w.sessionStorage.getItem('accessToken')) return false;

		return true;
	} catch {
		return true;
	}
}
